import React, { useState } from "react";
import API from "../Context/api";
import ErrorMessage from "../Helpers/ErrorMessage";

const AddUserForm = ({ setIsModalOpen, onUserAdded }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    role: "User", // Default role
  });

  const requiredFields = ["firstName", "lastName", "email", "password", "role"];

  const isFormValid = requiredFields.every(
    (field) => formData[field] && formData[field].trim() !== ""
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage(""); // Clear previous errors

    // Validation
    if (formData.firstName.length < 2 || formData.firstName.length > 50) {
      setErrorMessage("First name must be between 2 and 50 characters.");
      return;
    }

    if (formData.lastName.length < 2 || formData.lastName.length > 50) {
      setErrorMessage("Last name must be between 2 and 50 characters.");
      return;
    }

    // Validation
    if (!formData.role || formData.role === "") {
      setErrorMessage("Please select a role.");
      return;
    }

    if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      setErrorMessage("Invalid email format.");
      return;
    }
    if (formData.password.length < 8) {
      setErrorMessage("Password must be at least 8 characters long.");
      return;
    }

    setIsLoading(true);
    try {
      const response = await API.post("/users", formData);
      console.log("User created:", response.data);

      setFormData({ name: "", email: "", password: "", role: "user" }); // Reset form
      setIsModalOpen(false); // Close modal

      if (onUserAdded) {
        onUserAdded(response.data); // Pass the new user data to parent
      }
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message ||
          "An error occurred during user creation"
      );
      console.error("Error creating user:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-60 p-3 sm:p-4 z-50">
      <div className="w-full p-4 max-h-[80vh] max-w-md sm:max-w-lg lg:max-w-2xl bg-white rounded-xl shadow-lg overflow-hidden transform transition-all duration-300 scale-100 flex flex-col">
        {/* Header (Static) */}
        <div className="">
          {/* Static Header */}
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4">
            Add User
          </h2>
          <ErrorMessage message={errorMessage} />
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto space-y-3">
          <form>
            <div className="p-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName || ""}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                placeholder="Enter first name"
                required
              />
            </div>

            <div className="p-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName || ""}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                placeholder="Enter last name"
                required
              />
            </div>

            <div className="p-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                placeholder="Enter user email"
                required
              />
            </div>

            <div className="p-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                placeholder="Enter password"
                required
              />
            </div>

            <div className="p-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:outline-none"
              >
                <option value="">Select Role</option>
                <option value="User">User</option>
                <option value="Admin">Admin</option>
              </select>
            </div>
          </form>
        </div>

        {/* Action buttons */}
        <div className="flex justify-end gap-3 mt-2 p-2">
          <button
            type="button"
            onClick={() => setIsModalOpen(false)}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm font-medium transition-all duration-200 shadow-sm w-full sm:w-auto"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={!isFormValid || isLoading}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 shadow-sm w-full sm:w-auto ${
              !isFormValid || isLoading
                ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                : "bg-blue-500 text-white hover:bg-blue-600"
            }`}
          >
            {isLoading ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddUserForm;
